/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { loadScriptOnce } from '@/lib/tracking/load-script';
import { APPLE_PAY_TAG, buildApplePayConfig, getApplePayScriptUrl, requestApplePayRefresh } from './config';
import { cn } from '@/lib/utils';

interface ApplePayButtonProps {
    /**
     * PDP: SKU to buy; the platform builds an Apple Pay basket containing only this product.
     * Omit on cart / mini-cart to pay for the current basket (button shows only when its total > 0).
     */
    sku?: string;
    /** Hides the button, e.g. while no orderable variant is selected or the cart has inventory issues. */
    disabled?: boolean;
    /**
     * Cart / mini-cart: changes whenever the basket changes (e.g. basketId + total). SCAPI basket
     * updates don't touch the `dwapupreq` cookie SiteGenesis pipelines set, so the platform script
     * would keep showing a stale (or zero) total; bumping it makes the script re-fetch GetRequest.
     */
    refreshKey?: string;
    className?: string;
}

/**
 * "Buy with Apple Pay" button backed by native B2C Commerce Apple Pay, the same integration
 * SiteGenesis renders with `<isapplepay>` on the PDP (with `sku`), cart and mini-cart.
 *
 * The platform script polls the DOM every second for `<isapplepay>` and replaces each one
 * with a button, so the placeholder is created imperatively inside a React-owned container
 * (React must never reconcile a node the script removes). Merchant validation, basket,
 * shipping, payment authorization (CyberSource) and the order-confirmation redirect all run
 * in SiteGenesis via the `dw.extensions.applepay.*` hooks.
 */
export default function ApplePayButton({
    sku,
    disabled,
    refreshKey,
    className,
}: ApplePayButtonProps): ReactElement | null {
    const config = useConfig();
    const siteId = useSite()?.site?.id;
    const containerRef = useRef<HTMLDivElement>(null);
    const [canMakePayments, setCanMakePayments] = useState(false);

    const enabled = Boolean(siteId && config.hybrid?.enabled && config.hybrid?.applePay?.enabled);

    useEffect(() => {
        if (!enabled || !siteId) return;
        try {
            if (!window.ApplePaySession?.canMakePayments()) return;
        } catch {
            // canMakePayments throws on insecure origins
            return;
        }

        // The script reads window.dw.applepay once on load; set it first and never overwrite it.
        window.dw = window.dw ?? {};
        window.dw.applepay = window.dw.applepay ?? buildApplePayConfig(siteId);

        loadScriptOnce(getApplePayScriptUrl(siteId))
            .then(() => setCanMakePayments(true))
            .catch((error: unknown) => {
                // eslint-disable-next-line no-console
                console.error('[ApplePayButton] Failed to load B2C Apple Pay script', error);
            });
    }, [enabled, siteId]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !canMakePayments || disabled) return;

        const placeholder = document.createElement(APPLE_PAY_TAG);
        if (sku) placeholder.setAttribute('sku', sku);
        container.appendChild(placeholder);

        return () => {
            // Drop the placeholder or the button the script swapped in for it
            container.replaceChildren();
        };
    }, [canMakePayments, disabled, sku]);

    const lastRefreshKey = useRef(refreshKey);
    useEffect(() => {
        if (!canMakePayments || refreshKey === undefined || refreshKey === lastRefreshKey.current) return;
        lastRefreshKey.current = refreshKey;
        requestApplePayRefresh();
    }, [canMakePayments, refreshKey]);

    if (!enabled) return null;

    return <div ref={containerRef} data-testid="apple-pay-button" className={cn('apple-pay-express', className)} />;
}
